import { useQuery } from '@tanstack/react-query';
import { dataSourceRoot, getDataSource } from '../data/dataSourceFactory';
import {
  Tag,
  getCodeMeaning,
  getNumber,
  getSequence,
  getString,
  getStrings,
  type DicomJson,
} from '../data/dicomTags';
import type { Study } from '../data/types';
import { isDerivedDataset } from '../data/derivedSeries';
import { useSeriesMetadata } from './useSeriesMetadata';

export interface StudyThumbnail {
  bytes: ArrayBuffer;
  aspect: number; // width / height
}

/** WSI ImageType "flavors" that are slide *metadata*, not microscopy image data. */
const META_FLAVORS = new Set(['OVERVIEW', 'LABEL', 'THUMBNAIL']);

const WSI_SOP_CLASS_UID = '1.2.840.10008.5.1.4.1.1.77.1.6';

/**
 * Per-instance metadata for every series in a study, concatenated. Cached under
 * one key so the thumbnail and the image-count both reuse a single fetch. Fetched
 * lazily — pass `enabled` (e.g. true once the row scrolls into view).
 */
export function useStudySeriesInstances(studyInstanceUID: string, enabled: boolean) {
  return useQuery({
    queryKey: ['study-series-meta', dataSourceRoot(), studyInstanceUID],
    enabled,
    staleTime: Infinity,
    queryFn: async (): Promise<DicomJson[]> => {
      const ds = getDataSource();
      const seriesList = await ds.searchSeries(studyInstanceUID);
      const all: DicomJson[] = [];
      for (const sds of seriesList) {
        const seriesUID = getString(sds, Tag.SeriesInstanceUID);
        // An AI model's results (SEG, SR) are not images.
        if (!seriesUID || isDerivedDataset(sds)) continue;
        all.push(...(await ds.retrieveSeriesMetadata(studyInstanceUID, seriesUID)));
      }
      return all;
    },
  });
}

/** True for instances that are slide metadata (OVERVIEW/LABEL/THUMBNAIL). */
function isMetadataImage(inst: DicomJson): boolean {
  return getStrings(inst, Tag.ImageType).some((t) => META_FLAVORS.has(t.toUpperCase()));
}

/**
 * Count of viewable images in a study — the number of things actually worth
 * opening. A whole WSI slide (its whole pyramid of levels) counts as ONE image,
 * grouped by Frame of Reference (fallback: its series); OVERVIEW/LABEL/THUMBNAIL
 * are skipped, and so is what an AI model derived (SEG, SR). Every other
 * non-WSI instance counts individually. So a single slide → 1, three slides →
 * 3, and a slide plus a separate image → 2.
 */
export function countViewableImages(instances: DicomJson[]): number {
  const slides = new Set<string>();
  let others = 0;
  for (const inst of instances) {
    if (isMetadataImage(inst) || isDerivedDataset(inst)) continue;
    if (getString(inst, Tag.SOPClassUID) === WSI_SOP_CLASS_UID) {
      const slideKey =
        getString(inst, Tag.FrameOfReferenceUID) ||
        getString(inst, Tag.SeriesInstanceUID) ||
        '';
      slides.add(slideKey);
    } else {
      others += 1;
    }
  }
  return slides.size + others;
}

/**
 * A study whose images are stored as pyramids, so its instances are levels and
 * not images: a slide (SM), and an X-ray, which the converter tiles too and
 * which keeps its DX modality (convert sources/radiograph.py).
 */
const isTiledStudy = (study: Study): boolean => study.modalities.includes('SM') || study.modalities.includes('DX');

export interface ImageCount {
  count: number | undefined;
  loading: boolean;
}

/**
 * The "Images" count for a study row. Non-WSI studies use the standard
 * NumberOfStudyRelatedInstances immediately. WSI studies use the viewable-image
 * count (slides, not pyramid levels) derived from the per-study metadata shared
 * with the thumbnail — reporting `loading` until that metadata is in cache so the
 * cell can show a skeleton instead of flashing a wrong number. Never fetches itself.
 */
export function useStudyImageCount(study: Study, enabled: boolean): ImageCount {
  const isWsi = isTiledStudy(study);
  // Only WSI studies need the metadata round-trip (to collapse pyramid levels and
  // drop OVERVIEW/LABEL); others use the standard count for free. Lazy via `enabled`.
  const { data } = useStudySeriesInstances(study.studyInstanceUID, isWsi && enabled);
  if (!isWsi) return { count: study.numberOfInstances, loading: false };
  if (!data) return { count: undefined, loading: true };
  return { count: countViewableImages(data), loading: false };
}

/**
 * The "Series" count for a study row. QIDO's NumberOfStudyRelatedSeries counts
 * an AI model's results (SEG, SR) as series of the study, which they are to a
 * DICOM client; a WSI study here counts only its images' series, from the same
 * metadata the image count reads. Never fetches itself.
 */
export function useStudySeriesCount(study: Study, enabled: boolean): ImageCount {
  const isWsi = isTiledStudy(study);
  const { data } = useStudySeriesInstances(study.studyInstanceUID, isWsi && enabled);
  if (!isWsi) return { count: study.numberOfSeries, loading: false };
  if (!data) return { count: undefined, loading: true };
  const series = new Set(data.map((d) => getString(d, Tag.SeriesInstanceUID)).filter((u): u is string => !!u));
  return { count: series.size, loading: false };
}

export interface SeriesInfo {
  slideId?: string; // slide/container identifier (falls back to specimen id)
  anatomicSite?: string; // primary anatomic structure code meaning
  description?: string; // series description (falls back to specimen short description)
}

/** First non-empty value produced by `pick` across a set of instances. */
function firstNonEmpty(
  items: DicomJson[],
  pick: (m: DicomJson) => string | undefined,
): string | undefined {
  for (const it of items) {
    const v = pick(it)?.trim();
    if (v) return v;
  }
  return undefined;
}

/** Slide identification + anatomic site + description from a set of series
 *  instances. Some slides carry the specimen fields on a non-first instance,
 *  so we scan all instances. Pure — shared by the hook and the study header. */
export function deriveSeriesInfo(instances: DicomJson[]): SeriesInfo {
  const slideId =
    firstNonEmpty(instances, (m) => getString(m, Tag.ContainerIdentifier)) ??
    firstNonEmpty(instances, (m) =>
      getString(getSequence(m, Tag.SpecimenDescriptionSequence)[0], Tag.SpecimenIdentifier),
    );
  const anatomicSite = firstNonEmpty(instances, (m) =>
    getCodeMeaning(
      getSequence(m, Tag.SpecimenDescriptionSequence)[0],
      Tag.PrimaryAnatomicStructureSequence,
    ),
  );
  const description =
    firstNonEmpty(instances, (m) => getString(m, Tag.SeriesDescription)) ??
    firstNonEmpty(instances, (m) =>
      getString(getSequence(m, Tag.SpecimenDescriptionSequence)[0], Tag.SpecimenShortDescription),
    );
  return { slideId, anatomicSite, description };
}

/**
 * The LABEL image of one slide/series, for the right panel: strictly the
 * scanner's label photo, read from the already-cached series metadata (the
 * viewer fetched it for the slide on screen) and nothing else — null when
 * the slide has none, as a converted scanner file never does.
 *
 * `isLoading` is true until there is an answer — while the slide's metadata
 * is on its way, and while the image is fetched. Null is an answer, and so is
 * no slide at all (a study with nothing to show): nothing is on its way.
 */
export function useSeriesLabel(
  studyInstanceUID: string | undefined,
  seriesInstanceUID: string | undefined,
): { data: StudyThumbnail | null | undefined; isLoading: boolean } {
  const metaQuery = useSeriesMetadata(studyInstanceUID, seriesInstanceUID);
  const meta = metaQuery.data;
  const label = useQuery({
    queryKey: ['series-label', dataSourceRoot(), studyInstanceUID, seriesInstanceUID],
    enabled: !!studyInstanceUID && !!meta,
    staleTime: Infinity,
    queryFn: () =>
      loadThumbnail(
        studyInstanceUID!,
        (meta ?? []).find((m) => getStrings(m, Tag.ImageType).includes('LABEL')),
      ),
  });
  return {
    data: label.data,
    isLoading: !!studyInstanceUID && !!seriesInstanceUID && label.data === undefined && !label.isError && !metaQuery.isError,
  };
}

/** Fetch a thumbnail instance's frame 1 and crop off TILED_FULL tile padding. */
async function loadThumbnail(
  studyInstanceUID: string,
  pick: DicomJson | undefined,
): Promise<StudyThumbnail | null> {
  if (!pick) return null;
  const sop = getString(pick, Tag.SOPInstanceUID);
  const seriesUID = getString(pick, Tag.SeriesInstanceUID);
  if (!sop || !seriesUID) return null;
  const raw = await getDataSource().retrieveFrame(studyInstanceUID, seriesUID, sop, 1);
  if (!raw) return null;

  // A tiled VOLUME level is stored in a fixed tile (e.g. 256²) with the real
  // content in the top-left and the rest TILED_FULL padding. Crop to the actual
  // matrix so the thumbnail isn't mostly blank. OVERVIEW images carry no
  // TotalPixelMatrix tag, so they're left as-is.
  const frameCols = getNumber(pick, Tag.Columns) ?? 0;
  const frameRows = getNumber(pick, Tag.Rows) ?? 0;
  const contentCols = getNumber(pick, Tag.TotalPixelMatrixColumns) ?? frameCols;
  const contentRows = getNumber(pick, Tag.TotalPixelMatrixRows) ?? frameRows;
  const padded =
    contentCols > 0 && contentRows > 0 && (contentCols < frameCols || contentRows < frameRows);

  if (padded) {
    const cropped = await cropTopLeft(raw, contentCols, contentRows);
    if (cropped) return { bytes: cropped, aspect: contentCols / contentRows };
  }
  const w = contentCols || frameCols || 1;
  const h = contentRows || frameRows || 1;
  return { bytes: raw, aspect: w / Math.max(1, h) };
}

/** Crop the top-left w×h region out of a JPEG (drops TILED_FULL tile padding). */
async function cropTopLeft(
  bytes: ArrayBuffer,
  w: number,
  h: number,
): Promise<ArrayBuffer | null> {
  try {
    const bmp = await createImageBitmap(new Blob([bytes], { type: 'image/jpeg' }));
    const cw = Math.min(w, bmp.width);
    const ch = Math.min(h, bmp.height);
    const canvas = document.createElement('canvas');
    canvas.width = cw;
    canvas.height = ch;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      bmp.close?.();
      return null;
    }
    ctx.drawImage(bmp, 0, 0, cw, ch, 0, 0, cw, ch);
    bmp.close?.();
    const blob = await new Promise<Blob | null>((res) =>
      canvas.toBlob((b) => res(b), 'image/jpeg', 0.9),
    );
    return blob ? await blob.arrayBuffer() : null;
  } catch {
    return null;
  }
}
